<template>
  <dialog
    ref="dialogEl"
    class="scrim"
    aria-labelledby="donate-dialog-title"
    @cancel.prevent="emit('close')"
    @click="onScrimClick"
  >
    <div class="panel">
      <header class="head">
        <div>
          <p class="kicker">PayPal</p>
          <h2 id="donate-dialog-title">{{ t('support.title') }}</h2>
        </div>
        <CloseXButton :label="t('support.close')" @click="emit('close')" />
      </header>
      <p class="lead">{{ t('support.lead') }}</p>
      <p v-if="!signedIn" class="note">{{ t('support.signIn') }}</p>
      <div class="tiers">
        <button
          v-for="tier in DONATION_TIERS"
          :key="tier.id"
          type="button"
          class="tier"
          :disabled="busyId !== null"
          @click="pay(tier.id)"
        >
          <span class="price">{{ donationUsdLabel(tier.usdCents) }}</span>
          <span class="gain">
            <CoinToken size="sm" />
            <span>{{ t('support.rowCoins', { n: tier.coins }) }}</span>
            <span class="packs">{{ t('support.rowPacks', { n: donationPacks(tier.coins) }) }}</span>
          </span>
        </button>
      </div>
      <p v-if="failed" class="error" role="alert">{{ t('support.failed') }}</p>
      <div class="other">
        <a
          class="other-link"
          :href="SUPPORT_URL"
          target="_blank"
          rel="noopener noreferrer"
        >{{ t('support.other') }}</a>
        <p class="note">{{ t('support.otherNote') }}</p>
      </div>
    </div>
  </dialog>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  DONATION_TIERS,
  donationPacks,
  donationUsdLabel,
} from '@shared/index'
import { useI18n } from '../i18n/t'
import { useAuthStore } from '../stores/auth'
import { startDonation } from '../api/donations'
import { SUPPORT_URL } from '../supportUrl'
import CloseXButton from './CloseXButton.vue'
import CoinToken from './CoinToken.vue'

const emit = defineEmits<{ close: [] }>()

const { t } = useI18n()
const auth = useAuthStore()
const route = useRoute()
const router = useRouter()
const dialogEl = ref<HTMLDialogElement | null>(null)
const busyId = ref<string | null>(null)
const failed = ref(false)
const signedIn = computed(() => Boolean(auth.token))

onMounted(() => {
  dialogEl.value?.showModal()
})

function onScrimClick(event: MouseEvent): void {
  if (event.target === dialogEl.value) emit('close')
}

function signIn(): void {
  emit('close')
  const redirect = route.fullPath
  void router.push({
    name: 'login',
    query: redirect && redirect !== '/' ? { redirect } : {},
  })
}

async function pay(tierId: string): Promise<void> {
  if (!auth.token) {
    signIn()
    return
  }
  if (busyId.value) return
  busyId.value = tierId
  failed.value = false
  try {
    const { approveUrl } = await startDonation(auth.token, tierId)
    window.location.assign(approveUrl)
  } catch {
    failed.value = true
    busyId.value = null
  }
}
</script>

<style scoped>
.scrim {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: 100vh;
  max-width: 100vw;
  max-height: 100vh;
  margin: 0;
  padding: max(16px, env(safe-area-inset-top))
    max(16px, env(safe-area-inset-right))
    max(16px, env(safe-area-inset-bottom))
    max(16px, env(safe-area-inset-left));
  border: 0;
  background: transparent;
  overflow: hidden;
}

.scrim::backdrop {
  background: var(--md-scrim);
}

.panel {
  display: flex;
  flex-direction: column;
  width: min(420px, 100%);
  max-height: min(86vh, 720px);
  overflow: auto;
  padding: 20px 20px 16px;
  border-radius: 28px;
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
  box-shadow: var(--md-elevation-2);
}

.head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.kicker {
  margin: 0 0 4px;
  color: var(--md-sys-color-primary);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

h2 {
  margin: 0;
  font-size: 22px;
  font-weight: 400;
  line-height: 28px;
}

.lead,
.note {
  margin: 12px 0 0;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
  line-height: 20px;
}

.tiers {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
}

.tier {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 56px;
  padding: 10px 14px;
  border: 0;
  border-radius: 16px;
  background: #ffc439;
  color: #003087;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.tier:disabled {
  cursor: progress;
  opacity: 0.7;
}

.tier:focus-visible {
  outline: 2px solid var(--md-sys-color-primary);
  outline-offset: 2px;
}

.price {
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
}

.gain {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
}

.packs {
  font-weight: 500;
  opacity: 0.8;
}

.error {
  margin: 12px 0 0;
  color: var(--md-sys-color-error);
  font-size: 14px;
  line-height: 20px;
}

.other {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid var(--md-sys-color-outline-variant);
}

.other-link {
  color: var(--md-sys-color-primary);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.other-link:focus-visible {
  outline: 2px solid var(--md-sys-color-primary);
  outline-offset: 2px;
}

@media (prefers-reduced-motion: reduce) {
  .scrim::backdrop {
    backdrop-filter: none;
  }
}
</style>
